import { useQuery } from "@tanstack/react-query";
import type { SellerTransactionDetail } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";

type UseSellerTransactionQueryParams = {
  storeSlug: string;
  /** Null while no transaction is open — the query stays disabled. */
  transactionId: string | null;
};

type UseSellerTransactionQueryResult = {
  data: SellerTransactionDetail | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
};

export function useSellerTransactionQuery(
  params: UseSellerTransactionQueryParams,
): UseSellerTransactionQueryResult {
  const { storeSlug, transactionId } = params;
  const isEnabled = storeSlug.length > 0 && (transactionId?.length ?? 0) > 0;

  const query = useQuery({
    queryKey: queryKeys.seller.transactionById(storeSlug, transactionId ?? ""),
    queryFn: () =>
      apiJsonClient<SellerTransactionDetail>(
        endpoints.seller.transactionById(transactionId ?? ""),
      ),
    enabled: isEnabled,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}
